import config from 'config';
import * as repo from 'db/repositories';
import { refreshZoneIndex } from 'db/repositories';
import Data from 'model/Data';
import User from 'model/User';
import Pond from 'model/Pond';
import Visit from 'model/Visit';
import Image from 'model/Image';
import Zone from 'model/Zone';

/**
 * Orchestrateur de synchronisation API ↔ Dexie.
 * Chaîne légère (data/zones/user) + pull mine=1 ; cache public = navigation (carte/détail).
 */
var SyncManager = {

	resources: [
		{ name: 'data', label: 'Chargement des métadonnées…', type: 'singleton', table: 'data', Model: Data, url: config.url.data },
		{ name: 'zone_list', label: 'Chargement des départements…', type: 'collection', table: 'zones', Model: Zone, url: config.url.zone, idAttr: 'id' },
		{ name: 'user', label: 'Chargement du profil…', type: 'singleton', table: 'user', Model: User, url: config.url.user },
	],

	mineResources: [
		{ name: 'pond_list', label: 'Chargement de vos mares…', table: 'ponds', Model: Pond, url: config.url.pond },
		{ name: 'visit_list', label: 'Chargement de vos visites…', table: 'visits', Model: Visit, url: config.url.visit },
		{ name: 'image_list', label: 'Chargement de vos photos…', table: 'images', Model: Image, url: config.url.image },
	],

	timer: null,

	/**
	 * Lance une synchro complète (garde-fous + toast + chaîne).
	 */
	sync: function( msg )
	{
		clearTimeout( this.timer );
		this.timer = setTimeout( function()
		{
			app.sync();
		}, 1000 * 60 * 60 );

		// Hors ligne / verrouillé / déjà en cours
		if( app.is( 'locked' ) || app.is( 'working' ) || !app.is( 'online' ) )
		{
			if( !app.is( 'online' ) )
			{
				app.requestBackgroundSync();
			}
			return $.Deferred().resolve();
		}

		var deferred = $.Deferred();
		var self = this;

		app.set( 'synching', true );
		app.set( 'working', true );

		var finish = function( ok )
		{
			app.set( 'synching', false );
			app.set( 'working', false );
			app.trigger( 'synched' );
			if( ok )
			{
				deferred.resolve();
			}
			else
			{
				deferred.reject();
			}
		};

		// Migration one-shot puis chaîne légère + mine
		repo.ensureMineSyncMode().then( function()
		{
			self.syncChain().done( function()
			{
				self.syncMineChain().always( function()
				{
					finish( true );
				} );
			} ).fail( function()
			{
				finish( false );
			} );
		} ).catch( function( err )
		{
			console.error( 'ensureMineSyncMode', err );
			finish( false );
		} );

		var text = msg || 'Synchronisation des données…';
		app.addMessage( '<span class="sync-label">' + text + '</span> <span class="sync-count"></span>', deferred );

		return deferred;
	},

	/**
	 * Enchaîne data / zones / user.
	 */
	syncChain: function()
	{
		var chain = $.Deferred();
		var list = this.resources.slice();
		var self = this;

		var loop = function()
		{
			var res = list.shift();

			if( !res )
			{
				return chain.resolve();
			}

			if( res.label )
			{
				$( '.sync-label' ).text( res.label );
			}
			$( '.sync-count' ).text( '' );

			self.syncResource( res ).always( loop );
		};

		_.defer( loop );

		return chain.promise();
	},

	/**
	 * Push pending puis pull mine=1 (auth requise).
	 */
	syncMineChain: function()
	{
		var chain = $.Deferred();

		if( !app.is( 'authenticated' ) )
		{
			return chain.resolve().promise();
		}

		var userUuid = app.getStore( 'user' ).get( 'uuid' );
		if( !userUuid )
		{
			console.warn( 'syncMineChain: pas d\'user uuid, skip' );
			return chain.resolve().promise();
		}

		var list = this.mineResources.slice();
		var self = this;

		var loop = function()
		{
			var res = list.shift();

			if( !res )
			{
				return chain.resolve();
			}

			if( res.label )
			{
				$( '.sync-label' ).text( res.label );
			}
			$( '.sync-count' ).text( '' );

			// Push des brouillons locaux puis pull auteur
			self.pushCollection( res ).then( function()
			{
				return self.pullCollection( res, { mine: 1, userUuid: userUuid } );
			} ).always( loop );
		};

		_.defer( loop );

		return chain.promise();
	},

	/**
	 * Push puis pull pour une ressource légère (zones / singletons).
	 */
	syncResource: function( res )
	{
		var self = this;

		if( res.type === 'singleton' )
		{
			return self.pushSingleton( res ).then( function()
			{
				return self.pullSingleton( res );
			} );
		}

		return self.pushCollection( res ).then( function()
		{
			return self.pullCollection( res, {} );
		} );
	},

	/**
	 * Push d'un modèle singleton (user / data) depuis le store Backbone.
	 */
	pushSingleton: function( res )
	{
		var deferred = $.Deferred();
		var store = app.getStore( res.name === 'data' ? 'data' : 'user' );

		if( !store || !_.isFunction( store.commit ) )
		{
			deferred.resolve();
			return deferred.promise();
		}

		if( store.isSavable && store.isSavable() )
		{
			var op = store.get( 'todo' ) === 'delete' ? store.destroy() : store.save();

			if( !op )
			{
				deferred.resolve();
				return deferred.promise();
			}

			op.done( function()
			{
				store.unset( 'todo' );
			} ).always( function()
			{
				deferred.resolve();
			} );

			return deferred.promise();
		}

		deferred.resolve();
		return deferred.promise();
	},

	/**
	 * Pull singleton → store Backbone + Dexie.
	 */
	pullSingleton: function( res )
	{
		var deferred = $.Deferred();
		var store = app.getStore( res.name === 'data' ? 'data' : 'user' );

		if( !store )
		{
			deferred.resolve();
			return deferred.promise();
		}

		if( res.name === 'user' && !app.getStore( 'settings' ).hasToken() )
		{
			deferred.resolve();
			return deferred.promise();
		}

		var fetchOptions = {};
		if( res.name === 'user' && app.is( 'authenticated' ) )
		{
			fetchOptions.force = true;
		}

		store.fetch( fetchOptions ).always( function()
		{
			var attrs = store.toJSON();
			repo.saveSingleton( res.table, attrs ).then( function()
			{
				deferred.resolve();
			} ).catch( function()
			{
				deferred.resolve();
			} );
		} );

		return deferred.promise();
	},

	pushCollection: function( res )
	{
		var deferred = $.Deferred();
		var putFn = {
			zones: repo.putZone,
			ponds: repo.putPond,
			visits: repo.putVisit,
			images: repo.putImage,
		}[ res.table ];

		repo.getPending( res.table ).then( function( pending )
		{
			var collection = app.getStore( res.name );

			var loop = function()
			{
				var row = pending.shift();

				if( !row )
				{
					deferred.resolve();
					return;
				}

				var model = new res.Model( row );

				if( collection )
				{
					collection.add( model, { silent: true } );
				}

				var op = ( row.todo === 'delete' ) ? model.destroy() : model.save();

				if( !op )
				{
					if( collection )
					{
						collection.remove( model, { silent: true } );
					}
					_.defer( loop );
					return;
				}

				op.done( function()
				{
					var json = model.toJSON();
					delete json.todo;
					delete json.saving;

					if( row.todo === 'delete' )
					{
						return putFn( _.extend( {}, row, { deleted_at: Date.now() } ) );
					}

					return putFn( json );
				} ).always( function()
				{
					if( collection )
					{
						collection.remove( model, { silent: true } );
					}
					loop();
				} );
			};

			loop();
		} ).catch( function( err )
		{
			console.error( 'pushCollection', res.name, err );
			deferred.resolve();
		} );

		return deferred.promise();
	},

	/**
	 * Pull paginé API → Dexie.
	 * options.mine : 1 ; options.userUuid : curseur mine ; options.published : legacy zones.
	 */
	pullCollection: function( res, options )
	{
		options = options || {};
		var deferred = $.Deferred();
		var limit = 1000;
		var offset = 0;
		var total = null;
		var allStamps = [];
		var userUuid = options.userUuid || null;
		var published = options.published;
		var mine = options.mine;

		repo.getSince( res.table, userUuid ).then( function( since )
		{
			var finish = function()
			{
				$( '.sync-count' ).text( '' );

				repo.bumpSince( res.table, allStamps, userUuid ).then( function()
				{
					if( res.table === 'zones' )
					{
						return refreshZoneIndex();
					}
				} ).then( function()
				{
					deferred.resolve();
				} ).catch( function()
				{
					deferred.resolve();
				} );
			};

			var fetchPage = function()
			{
				var data = { offset: offset, limit: limit };
				if( since != null )
				{
					data.since = since;
				}
				if( published === 0 || published === 1 )
				{
					data.published = published;
				}
				if( mine === 1 )
				{
					data.mine = 1;
				}

				$.ajax( {
					url: res.url,
					method: 'GET',
					dataType: 'json',
					data: data,
				} ).done( function( json )
				{
					var list = _.isArray( json ) ? json : ( json && json.data ? json.data : [] );

					if( json && json.total != null )
					{
						total = json.total;
					}

					if( total != null && total > 0 )
					{
						var loaded = offset + list.length;
						var percent = Math.min( 100, Math.round( 100 * loaded / total ) );
						$( '.sync-count' ).text( percent + '%' );
					}

					var writer = {
						zones: repo.putZones,
						ponds: repo.putPonds,
						visits: repo.putVisits,
						images: repo.putImages,
					}[ res.table ];

					var write = list.length ? writer( list ) : Promise.resolve();

					write.then( function()
					{
						_.each( list, function( row )
						{
							var t = row.deleted_at || row.updated_at;
							if( _.isString( t ) && !/^\d+$/.test( t ) )
							{
								var parsed = Date.parse( t );
								if( !isNaN( parsed ) )
								{
									allStamps.push( parsed );
								}
							}
							else if( _.isNumber( t ) )
							{
								allStamps.push( t );
							}
							else if( t )
							{
								allStamps.push( Number( t ) );
							}
						} );

						if( json && json.has_more )
						{
							offset += limit;
							fetchPage();
							return;
						}

						finish();
					} ).catch( function( err )
					{
						console.error( 'pullCollection write', res.name, err );
						deferred.resolve();
					} );
				} ).fail( function( xhr )
				{
					$( '.sync-count' ).text( '' );

					if( xhr && xhr.status === 401 )
					{
						// Pull mine sans auth : ne pas forcer signout si déjà en logout
						if( mine !== 1 && published !== 0 )
						{
							app.signout();
						}
					}
					else
					{
						console.error( 'pullCollection', res.name, xhr );
					}

					deferred.resolve();
				} );
			};

			fetchPage();
		} ).catch( function( err )
		{
			console.error( 'pullCollection since', res.name, err );
			deferred.resolve();
		} );

		return deferred.promise();
	},
};

export default SyncManager;
